'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const highlights = [
{
  id: 1,
  title: '6 Days of Celebration',
  subtitle: 'Oct 16 – 21, 2026',
  description: 'From Shoshthi Bodhan to Dashami Sindoor Khela - six days of sacred rituals, cultural performances, and community joy.',
  image: "/assets/images/7_days_celebration.png",
  alt: 'Ornate Durga idol on stage flanked by Patachitra art panels at HCAF Durga Puja',
  icon: '🪔',
  span: 'row-span-2'
},
{
  id: 2,
  title: 'Gala Evening',
  subtitle: 'Oct 20 · Shanmukhananda Chandrasekhara Saraswati Auditorium',
  description: 'Live with Javed Ali — 2,763 seats, one night only.',
  image: "/assets/images/gala_evening.png",
  alt: 'Singers performing on stage at HCAF Gala Evening with colorful stage lighting',
  icon: '🎵',
  span: ''
},
{
  id: 3,
  title: 'Anondo Mela',
  subtitle: 'Food Bazaar · Shashthi Evening',
  description: 'Housewives cook and sell from their homes - a real community kitchen, not a catering contract.',
  image: "/assets/images/anondo_mela.png",
  alt: 'Community food bazaar at Anondo Mela with guests being served home-cooked Bengali food',
  icon: '🍛',
  span: ''
}];


const sindoor = {
  id: 4,
  title: 'Sindoor Khela',
  subtitle: 'Dashami · Oct 21',
  description: 'Women from every walk of life come together in a sea of colour - two hours of live dhaak and ritual colour play, the most photographed moment of the festival.',
  image: "/assets/images/sindoor_khela.png",
  alt: 'Women celebrating Sindoor Khela with vibrant red sindoor powder against bright natural lighting',
  icon: '🌺'
};

export default function EventHighlightsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto mb-8 md:mb-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 reveal">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full border border-border text-xs font-bold text-muted-foreground tracking-widest uppercase mb-3 md:mb-4">
              Event Highlights
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight leading-tight">
              The Prathaa <span className="text-primary">Experience</span>
            </h2>
          </div>
          <Link
            href="/event"
            className="inline-flex items-center gap-2 text-sm font-bold text-foreground border-b-2 border-primary pb-0.5 hover:text-primary transition-colors duration-200 self-start md:self-auto">
            
            Full Schedule →
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">

        {/* Card 1: 6 Days — row-span-2 */}
        <div className="md:row-span-2 relative overflow-hidden rounded-3xl md:rounded-4xl group card-depth reveal">
          <div className="relative h-56 md:h-full min-h-[300px] md:min-h-[420px] overflow-hidden">
            <AppImage
              src={highlights?.[0]?.image}
              alt={highlights?.[0]?.alt}
              fill
              className="object-cover grayscale-hover"
              sizes="(max-width: 768px) 100vw, 33vw" />
          </div>
          {/* Default: title only at bottom */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7 transition-opacity duration-300 group-hover:opacity-0">
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/80 via-transparent to-transparent" />
            <h3 className="relative font-display text-xl md:text-2xl font-black text-white tracking-tight">{highlights?.[0]?.title}</h3>
          </div>
          {/* Hover: translucent grey overlay with full content */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7 bg-gray-800/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-2xl md:text-3xl mb-2 md:mb-3">{highlights?.[0]?.icon}</span>
            <span className="text-accent text-xs font-bold tracking-widest uppercase mb-1 md:mb-2">{highlights?.[0]?.subtitle}</span>
            <h3 className="font-display text-xl md:text-2xl font-black text-white mb-2 tracking-tight">{highlights?.[0]?.title}</h3>
            <p className="text-white/90 text-sm leading-relaxed">{highlights?.[0]?.description}</p>
          </div>
        </div>

        {/* Card 2: Gala Evening */}
        <div className="relative overflow-hidden rounded-3xl md:rounded-4xl group card-depth reveal reveal-delay-100">
          <div className="relative h-48 md:h-56 overflow-hidden">
            <AppImage
              src={highlights?.[1]?.image}
              alt={highlights?.[1]?.alt}
              fill
              className="object-cover grayscale-hover"
              sizes="(max-width: 768px) 100vw, 33vw" />
          </div>
          {/* Default: title only at bottom */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6 transition-opacity duration-300 group-hover:opacity-0">
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/80 via-transparent to-transparent" />
            <h3 className="relative font-display text-lg md:text-xl font-black text-white tracking-tight">{highlights?.[1]?.title}</h3>
          </div>
          {/* Hover: translucent grey overlay with full content */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6 bg-gray-800/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-2xl mb-2">{highlights?.[1]?.icon}</span>
            <span className="text-accent text-xs font-bold tracking-widest uppercase mb-1">{highlights?.[1]?.subtitle}</span>
            <h3 className="font-display text-lg md:text-xl font-black text-white mb-1 tracking-tight">{highlights?.[1]?.title}</h3>
            <p className="text-white/90 text-sm leading-relaxed">{highlights?.[1]?.description}</p>
          </div>
        </div>

        {/* Card 3: Anondo Mela */}
        <div className="relative overflow-hidden rounded-3xl md:rounded-4xl group card-depth reveal reveal-delay-200">
          <div className="relative h-48 md:h-56 overflow-hidden">
            <AppImage
              src={highlights?.[2]?.image}
              alt={highlights?.[2]?.alt}
              fill
              className="object-cover grayscale-hover"
              sizes="(max-width: 768px) 100vw, 33vw" />
          </div>
          {/* Default: title only at bottom */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6 transition-opacity duration-300 group-hover:opacity-0">
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/80 via-transparent to-transparent" />
            <h3 className="relative font-display text-lg md:text-xl font-black text-white tracking-tight">{highlights?.[2]?.title}</h3>
          </div>
          {/* Hover: translucent grey overlay with full content */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6 bg-gray-800/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-2xl mb-2">{highlights?.[2]?.icon}</span>
            <span className="text-accent text-xs font-bold tracking-widest uppercase mb-1">{highlights?.[2]?.subtitle}</span>
            <h3 className="font-display text-lg md:text-xl font-black text-white mb-1 tracking-tight">{highlights?.[2]?.title}</h3>
            <p className="text-white/90 text-sm leading-relaxed">{highlights?.[2]?.description}</p>
          </div>
        </div>

        {/* Card 4: Sindoor Khela — col-span-2 */}
        <div className="md:col-span-2 relative overflow-hidden rounded-3xl md:rounded-4xl group card-depth reveal reveal-delay-300">
          <div className="relative h-48 md:h-56 overflow-hidden">
            <AppImage
              src={sindoor?.image}
              alt={sindoor?.alt}
              fill
              className="object-cover grayscale-hover"
              sizes="(max-width: 768px) 100vw, 66vw" />
          </div>
          {/* Default: title only at bottom */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7 transition-opacity duration-300 group-hover:opacity-0">
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/80 via-transparent to-transparent" />
            <h3 className="relative font-display text-xl md:text-2xl font-black text-white tracking-tight">{sindoor?.title}</h3>
          </div>
          {/* Hover: translucent grey overlay with full content */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7 bg-gray-800/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="flex items-end justify-between">
              <div>
                <span className="text-2xl mb-2 block">{sindoor?.icon}</span>
                <span className="text-accent text-xs font-bold tracking-widest uppercase mb-1 block">{sindoor?.subtitle}</span>
                <h3 className="font-display text-xl md:text-2xl font-black text-white mb-2 tracking-tight">{sindoor?.title}</h3>
                <p className="text-white/90 text-sm leading-relaxed max-w-md">{sindoor?.description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}